import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveHeader,
  ResponsiveFooter,
} from '../ui/ResponsiveLayout';
import {
  HouseIcon,
  HotelIcon,
  MountainSunIcon,
  PlusCircleIcon,
  DocumentsIcon,
} from '../ui/Icons';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { SubHeadline } from '../ui/Typography';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';
import { removeAssetDistribution, type DistributionMap } from '../../utils/distributionUtils';

interface Property {
  id: string;
  type: 'house' | 'apartment' | 'land' | 'commercial' | 'other';
  address: string;
  description?: string;
  purchasePrice?: number;
  currentValue?: number;
  mortgageBalance: number;
  monthlyPayment?: number;
  notes?: string;
  lat?: number;
  lon?: number;
  displayName?: string;
  documents?: AppDocument[];
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame190: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [properties, setProperties] = useState<Property[]>(() => {
    const sessionProps = session.taskProgress['properties']?.answers?.properties || [];
    if (Array.isArray(sessionProps) && sessionProps.length > 0) {
      return sessionProps.map((p: unknown) => ({
        id: p.id || Date.now().toString(),
        type: p.type || 'house',
        address: p.address || 'Unknown Address',
        mortgageBalance: typeof p.mortgageBalance === 'number' ? p.mortgageBalance : 0,
        notes: p.notes || '',
        lat: p.lat,
        lon: p.lon,
        displayName: p.displayName,
        documents: normalizeDocuments(p.documents || []),
      }));
    }

    try {
      const backup = localStorage.getItem('properties-backup');
      if (backup) {
        const parsed = JSON.parse(backup);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((p: unknown) => ({
            id: p.id || Date.now().toString(),
            type: p.type || 'house',
            address: p.address || 'Unknown Address',
            mortgageBalance: typeof p.mortgageBalance === 'number' ? p.mortgageBalance : 0,
            notes: p.notes || '',
            lat: p.lat,
            lon: p.lon,
            displayName: p.displayName,
            documents: normalizeDocuments(p.documents || []),
          }));
        }
      }
    } catch (error) {
      console.warn('[Frame190] Failed to load from backup:', error);
    }

    return [];
  });

  const saveToLocalStorage = (propertiesData: Property[]) => {
    try {
      localStorage.setItem('properties-backup', JSON.stringify(propertiesData));
      localStorage.setItem(
        'properties-data-backup',
        JSON.stringify({
          properties: propertiesData,
          lastUpdated: new Date().toISOString(),
          taskId: 'properties',
        })
      );
    } catch (error) {
      console.error('[Frame190] IMMEDIATE SAVE FAILED:', error);
    }
  };

  const updateProperties = (newProperties: Property[]) => {
    setProperties(newProperties);
    saveToLocalStorage(newProperties);
    updateTaskProgress('properties', { properties: newProperties }).catch((error) =>
      console.warn('[Frame190] Session save failed:', error)
    );
  };

  useEffect(() => {
    const sessionProps = session.taskProgress['properties']?.answers?.properties || [];
    if (sessionProps.length > 0 && JSON.stringify(sessionProps) !== JSON.stringify(properties)) {
      const normalizedProps = sessionProps.map((p: unknown) => ({
        ...p,
        documents: normalizeDocuments(p.documents || []),
      }));
      setProperties(normalizedProps);
      saveToLocalStorage(normalizedProps);
    }
  }, [session.taskProgress['properties']?.answers?.properties]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('Add Property');
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);

  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [addr, setAddr] = useState('');
  const [mortgage, setMortgage] = useState('');
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const [geo, setGeo] = useState<{ lat: number; lon: number; display_name: string } | null>(null);
  const [addressSuggestions, setAddressSuggestions] = useState<
    Array<{ lat: number; lon: number; display_name: string }>
  >([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const addressInputRef = React.useRef<HTMLDivElement>(null);

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame190] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const formatCurrencyInput = (val: string) => {
    const digits = val.replace(/[^0-9]/g, '');
    if (!digits) return '';
    return parseInt(digits, 10).toLocaleString();
  };

  const fetchAddressSuggestions = async (
    query: string
  ): Promise<Array<{ lat: number; lon: number; display_name: string }>> => {
    if (!query || query.length < 3) return [];
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`,
        { headers: { 'User-Agent': 'End-of-Era-Estate-Planning/1.0' } }
      );
      if (!response.ok) return [];
      const data = await response.json();

      return data
        .filter((item: unknown) => item.address && (item.address.road || item.address.house_number))
        .map((item: unknown) => {
          const a = item.address;
          const houseNumber = a?.house_number ? a.house_number.replace(/,/g, '').trim() : '';
          const street = a?.road || '';
          const primaryLine = `${houseNumber} ${street}`.trim();
          const city = a?.city || a?.town || a?.village || '';
          const state = a?.state || '';
          const zip = a?.postcode || '';
          const country = a?.country || '';

          const normalized = [primaryLine, city, state, zip, country]
            .filter(Boolean)
            .join(', ')
            .replace(/,\s*,/g, ',');

          return {
            lat: parseFloat(item.lat),
            lon: parseFloat(item.lon),
            display_name: normalized,
          };
        });
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (!addr || addr.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const handle = setTimeout(async () => {
      setIsFetchingSuggestions(true);
      const suggestions = await fetchAddressSuggestions(addr);
      setAddressSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      setIsFetchingSuggestions(false);
    }, 500);
    return () => clearTimeout(handle);
  }, [addr]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressInputRef.current && !addressInputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectSuggestion = (suggestion: {
    lat: number;
    lon: number;
    display_name: string;
  }) => {
    setAddr(suggestion.display_name);
    setGeo(suggestion);
    setShowSuggestions(false);
  };

  const resetForm = () => {
    setAddr('');
    setMortgage('');
    setNotes('');
    setGeo(null);
    setDocuments([]);
    setIsUploadingDocuments(false);
    setAddressSuggestions([]);
    setShowSuggestions(false);
    setEditingProperty(null);
  };

  const handleOk = async () => {
    if (!addr) {
      alert('Address required');
      return;
    }

    const mortgageValue = parseFloat(mortgage.replace(/,/g, '') || '0');

    try {
      if (editingProperty) {
        const updatedProp: Property = {
          ...editingProperty,
          address: addr,
          mortgageBalance: mortgageValue,
          notes,
          lat: geo?.lat,
          lon: geo?.lon,
          displayName: geo?.display_name || addr,
          documents,
        };
        updateProperties(properties.map((p) => (p.id === editingProperty.id ? updatedProp : p)));
      } else {
        const typeFromTitle: Property['type'] =
          modalTitle === 'Land'
            ? 'land'
            : modalTitle === 'Rental property'
              ? 'apartment'
              : modalTitle === 'Add another property'
                ? 'other'
                : 'house';

        const newProp: Property = {
          id: Date.now().toString(),
          type: typeFromTitle || 'house',
          address: addr,
          mortgageBalance: mortgageValue,
          notes,
          lat: geo?.lat,
          lon: geo?.lon,
          displayName: geo?.display_name || addr,
          documents,
        };
        updateProperties([...properties, newProp]);
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('[Frame190] Failed to save property:', error);
      alert('Failed to save property. Please try again.');
    }
  };

  const handleEdit = (property: Property) => {
    setEditingProperty(property);
    setAddr(property.address);
    setMortgage(property.mortgageBalance.toLocaleString());
    setNotes(property.notes || '');
    setDocuments(normalizeDocuments(property.documents || []));
    if (property.lat && property.lon) {
      setGeo({
        lat: property.lat,
        lon: property.lon,
        display_name: property.displayName || property.address,
      });
    } else {
      setGeo(null);
    }
    setModalTitle('Edit Property');
    setModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    const propertyToDelete = properties.find((p) => p.id === deleteId);
    const attachedDocs = propertyToDelete?.documents || [];

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame190] Failed to delete document while deleting property:', error);
      }
    }

    // Cascade: remove distribution record for the deleted property.
    const currentDist = (session.taskProgress?.assetDistribution?.answers?.distributions ?? {}) as DistributionMap;
    if (currentDist[deleteId]) {
      const cleanedDist = removeAssetDistribution(currentDist, deleteId);
      await updateTaskProgress('assetDistribution', { distributions: cleanedDist });
    }

    updateProperties(properties.filter((p) => p.id !== deleteId));
    setDeleteId(null);
  };

  const handleRemoveAttachedDocument = async (propertyId: string, documentIndex: number) => {
    const property = properties.find((p) => p.id === propertyId);
    const docToRemove = property?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame190] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedProperties = properties.map((property) => {
      if (property.id !== propertyId) return property;
      return {
        ...property,
        documents: normalizeDocuments(property.documents || []).filter((_, index) => index !== documentIndex),
      };
    });

    updateProperties(updatedProperties);
  };

  const removeDocumentFromForm = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame190] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Properties" />
        <SubHeadline>
          Add information about any properties you own, including your primary residence, rental
          properties, and land. This information will help your loved ones manage these assets.
        </SubHeadline>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            {
              key: 'primary',
              label: 'Primary residence',
              Icon: HouseIcon,
              sub: 'Your main residence',
            },
            {
              key: 'rental',
              label: 'Rental property',
              Icon: HotelIcon,
              sub: 'Income-generating property',
            },
            { key: 'land', label: 'Land', Icon: MountainSunIcon, sub: 'Undeveloped property' },
            {
              key: 'add',
              label: 'Add another property',
              Icon: PlusCircleIcon,
              sub: 'Add another property type',
            },
          ].map(({ key, label, Icon, sub }) => (
            <button
              key={key}
              className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
              onClick={() => {
                resetForm();
                setModalTitle(label);
                setModalOpen(true);
              }}
            >
              <div className="flex items-center gap-4">
                <Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-stone-800 leading-tight">{label}</h3>
                  <p className="text-sm text-stone-600 mt-1">{sub}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {properties.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-medium text-stone-800 mb-4">
              Your Properties ({properties.length})
            </h2>
            <div className="space-y-4">
              {properties.map((p) => {
                const PropertyIcon =
                  p.type === 'land' ? MountainSunIcon : p.type === 'apartment' ? HotelIcon : HouseIcon;

                return (
                  <div
                    key={p.id}
                    className="bg-white rounded-lg border border-neutral-200 p-4"
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-4">
                        <PropertyIcon className="w-6 h-6 text-cyan-700 flex-shrink-0" />

                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-stone-800 break-words">
                            {p.displayName || p.address}
                          </div>

                          <div className="text-sm text-stone-500 break-words">
                            Mortgage Balance: ${p.mortgageBalance.toLocaleString()}
                          </div>

                          {p.notes && (
                            <div className="text-sm text-stone-600 mt-2 break-words">{p.notes}</div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                        <button
                          onClick={() => handleEdit(p)}
                          className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteId(p.id)}
                          className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {p.documents && p.documents.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <div className="text-sm text-cyan-700 font-medium">
                          Attached Documents ({p.documents.length})
                        </div>

                        <div className="space-y-2">
                          {normalizeDocuments(p.documents).map((doc, index) => (
                            <div
                              key={`${p.id}-doc-${doc.id}-${index}`}
                              className="flex flex-col gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <button
                                type="button"
                                onClick={() => openDocument(doc)}
                                className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                              >
                                <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                                <span
                                  className="text-sm font-medium text-stone-800 truncate"
                                  title={doc.name}
                                >
                                  {doc.name}
                                </span>
                              </button>

                              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                                <button
                                  type="button"
                                  onClick={() => downloadDocument(doc)}
                                  className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                >
                                  Download
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleRemoveAttachedDocument(p.id, index)}
                                  className="text-red-500 hover:text-red-700 text-xs shrink-0"
                                  aria-label={`Remove ${doc.name}`}
                                  title="Remove document"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </ResponsiveContent>

      <ResponsiveFooter
        title="Inventory Your Assets"
        subtitle="Add property and real estate details"
        progressPercent={75}
        onNext={() => {
          navigate('/frame-239');
        }}
        onBack={() => navigate('/frame-191')}
        nextText="Next"
        backText="Back"
      />

      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          resetForm();
        }}
        title={modalTitle}
        okText={editingProperty ? 'Save Changes' : 'Add Property'}
        cancelText="Cancel"
        onOk={handleOk}
        onCancel={() => {
          setModalOpen(false);
          resetForm();
        }}
      >
        <div className="space-y-4 pr-1">
          <div className="relative" ref={addressInputRef}>
            <label className="block text-sm font-medium mb-1">Address</label>
            <input
              value={addr}
              onChange={(e) => setAddr(e.target.value)}
              className="w-full border border-neutral-300 rounded px-3 py-2"
              placeholder="Start typing an address..."
            />
            {isFetchingSuggestions && (
              <div className="absolute right-3 top-9 animate-spin h-4 w-4 border-2 border-cyan-600 border-t-transparent rounded-full" />
            )}
            {showSuggestions && addressSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-md shadow-lg overflow-hidden">
                {addressSuggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => handleSelectSuggestion(s)}
                    className="w-full text-left px-3 py-2 hover:bg-cyan-50 text-sm border-b last:border-0"
                  >
                    {s.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {geo && (
            <div className="space-y-2">
              <iframe
                title="map-preview"
                className="w-full h-48 rounded border"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${geo.lon - 0.005}%2C${geo.lat - 0.005}%2C${geo.lon + 0.005}%2C${geo.lat + 0.005}&layer=mapnik&marker=${geo.lat}%2C${geo.lon}`}
              />
              <div className="flex justify-start">
                <a
                  href={`https://www.openstreetmap.org/?mlat=${geo.lat}&mlon=${geo.lon}#map=16/${geo.lat}/${geo.lon}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-cyan-700 hover:text-cyan-800 underline flex items-center gap-1"
                >
                  View larger map
                </a>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Mortgage Balance ($)</label>
            <input
              value={mortgage}
              onChange={(e) => setMortgage(formatCurrencyInput(e.target.value))}
              className="w-full border border-neutral-300 rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-neutral-300 rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Upload Documents
            </label>
            <FileUpload
              onUpload={async (newFiles) => {
                if (!newFiles.length) return;

                setIsUploadingDocuments(true);

                try {
                  const uploadedDocs = await Promise.all(
                    newFiles.map((file) => documentService.upload(file))
                  );

                  setDocuments((prev) => [...prev, ...uploadedDocs]);
                } catch (error) {
                  console.error('[Frame190] Failed to upload document(s):', error);
                  alert('Failed to upload one or more documents.');
                } finally {
                  setIsUploadingDocuments(false);
                }
              }}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              multiple
            />

            {isUploadingDocuments && (
              <div className="mt-3 text-sm text-stone-500">
                Uploading documents...
              </div>
            )}

            {documents.length > 0 && (
              <ul className="mt-2 space-y-2">
                {documents.map((doc, index) => (
                  <li
                    key={`${doc.id}-${index}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-stone-600 bg-stone-50 p-2 border border-stone-200 rounded"
                  >
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                    >
                      <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0" />

                      <span className="text-sm font-medium text-cyan-700 hover:text-cyan-800 truncate" title={doc.name}>
                        {doc.name}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                      >
                        Download
                      </button>

                      <button
                        onClick={() => removeDocumentFromForm(index)}
                        className="ml-2 text-red-600 hover:text-red-700 text-xs shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

      </Modal>

      <Modal 
      open={!!deleteId} 
      onClose={() => setDeleteId(null)}
      title="Delete Property?"
      okText="Delete Property"
      cancelText="Cancel"
      onOk={confirmDelete}
      onCancel={() => setDeleteId(null)}
      >
        <div className="p-1">
          <p className="text-stone-600 mb-6">
            Are you sure you want to remove this from your properties? This cannot be undone.
          </p>
          
        </div>
      </Modal>
    </ResponsiveContainer>
  );
};

export default Frame190;